import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import RSVP_OPEN from '../config/rsvp';
import { db } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import GUEST_LIST from '../data/guestList';
import { EMPTY_STATUS, attendsAnyEvent, sanitizeStatus, entryToDocId } from '../utils/rsvpStatus';

// ── Weekend events & dinner menu ─────────────────────────────────────────────
const MEAL_OPTIONS = [
  { value: 'fish',       label: 'Chilean Seabass' },
  { value: 'meat',       label: 'Beef Tenderloin' },
  { value: 'vegetarian', label: 'Vegetable Lasagna', desc: 'Vegetarian' },
];
const MEAL_LABEL = MEAL_OPTIONS.reduce((m, o) => ({ ...m, [o.value]: o.label }), {});

// Rehearsal Dinner invitees: entry.rehearsal === true → whole party;
// entry.rehearsal === ["Name", …] → only those members of the party.
function invitedToRehearsal(entry, name) {
  if (!entry?.rehearsal) return false;
  return Array.isArray(entry.rehearsal) ? entry.rehearsal.includes(name) : true;
}

// ── Helper: is a person's RSVP complete? ─────────────────────────────────────
function isPersonComplete(s, rehearsalInvited) {
  if (!s || !s.welcomeParty || !s.attending) return false;
  if (rehearsalInvited && !s.rehearsal) return false;
  if (s.attending === 'accepts' && !s.meal) return false;
  if (attendsAnyEvent(s) && !s.dietary) return false;
  return true;
}

// ── Confirm message ───────────────────────────────────────────────────────────
function getConfirmMessage(statuses) {
  const vals = Object.values(statuses);
  if (vals.every(v => v?.attending === 'accepts'))  return "We can't wait to celebrate with you in Naples!";
  if (vals.every(v => v?.attending === 'declines')) return "We'll miss you so much. Thank you for being in our lives.";
  if (vals.some(v => v?.attending === 'accepts'))   return "So glad some of you can make it — we'll celebrate together!";
  return "Thank you for letting us know. We'll be in touch!";
}

// ── Search rate limiting ──────────────────────────────────────────────────────
const SEARCH_LIMIT_KEY = 'ferry_rsvp_searches';
const MAX_SEARCHES = 7;
const LOCKOUT_MS = 60 * 60 * 1000; // 1 hour

function getSearchTracker() {
  try { return JSON.parse(localStorage.getItem(SEARCH_LIMIT_KEY) || '{}'); }
  catch { return {}; }
}
function setSearchTracker(data) {
  localStorage.setItem(SEARCH_LIMIT_KEY, JSON.stringify(data));
}
function clearSearchTracker() {
  localStorage.removeItem(SEARCH_LIMIT_KEY);
}
function isSearchLocked() {
  const t = getSearchTracker();
  if (t.lockedUntil && Date.now() < t.lockedUntil) return t.lockedUntil;
  if (t.lockedUntil && Date.now() >= t.lockedUntil) { clearSearchTracker(); }
  return false;
}
function incrementSearch() {
  const t = getSearchTracker();
  const count = (t.count || 0) + 1;
  if (count >= MAX_SEARCHES) {
    setSearchTracker({ count, lockedUntil: Date.now() + LOCKOUT_MS });
    return true; // now locked
  }
  setSearchTracker({ count });
  return false;
}

// ── Storage ───────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'ferry_rsvp_2026';
function loadAllRsvps() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
}
function savePartyRsvp(personStatuses) {
  // Keep localStorage for instant UX (guest sees their response immediately)
  const all = loadAllRsvps();
  Object.entries(personStatuses).forEach(([name, status]) => {
    if (status) all[name] = status;
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}
function clearPartyRsvp(guestNames) {
  const all = loadAllRsvps();
  guestNames.forEach(name => { delete all[name]; });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

// Write party responses to Firestore
async function savePartyRsvpRemote(entry, personStatuses) {
  try {
    const docId = entryToDocId(entry);
    await setDoc(doc(db, 'rsvps', docId), {
      party:      entry,
      responses:  personStatuses,
      updatedAt:  serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    // Non-fatal — localStorage still has the response locally
    console.error('Firestore write failed:', err);
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
// /rsvp after the deadline: old links and bookmarks still land somewhere
// friendly, but no late response can come in without us knowing about it.
function RsvpClosed() {
  return (
    <div className="site-wrapper">
      <div className="rsvp-fullpage">
        <div className="rsvp-bg-photo" style={{ backgroundImage: "url('/RSVP.png')" }} />
        <div className="rsvp-bg-overlay" />

        <div className="rsvp-content">
          <div className="rsvp-card">
            <div className="rsvp-save-the-date-row">
              <img
                src="/Flamingo Pointing.jpeg"
                alt="Flamingo pointing"
                className="rsvp-mascot rsvp-mascot--left"
              />
              <h2 style={{ fontSize: '60px', marginBottom: '0', lineHeight: 1.2, padding: 0 }}>RSVP</h2>
              <img
                src="/Gator Pointing.jpeg"
                alt="Gator pointing"
                className="rsvp-mascot rsvp-mascot--right"
              />
            </div>
            <div style={{ fontSize: '20px', color: 'grey', textAlign: 'center', marginBottom: '24px', fontFamily: 'var(--font-script)' }}>October 30, 2026 · Naples, Florida</div>

            <p style={{ fontSize: '1rem', color: '#374151', textAlign: 'center', lineHeight: 1.6, marginBottom: '12px' }}>
              Thank you to everyone who replied — RSVPs closed on September&nbsp;18, 2026,
              and we're busy putting the final details together.
            </p>
            <p style={{ fontSize: '0.88rem', color: '#6b7280', textAlign: 'center', lineHeight: 1.6, marginBottom: '28px' }}>
              If you still need to respond, or something about your response has changed,
              please reach out to Caroline and Michael directly and we'll take care of it.
            </p>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <Link to="/schedule" className="btn btn-gold">View Schedule</Link>
              <Link to="/" className="btn btn-outline">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function RSVP() {
  return RSVP_OPEN ? <RsvpForm /> : <RsvpClosed />;
}

function RsvpForm() {
  const [query,          setQuery]          = useState('');
  const [searchResults,  setSearchResults]  = useState([]);
  const [searched,       setSearched]       = useState(false);
  const [selectedEntry,  setSelectedEntry]  = useState(null);
  const [parsedGuests,   setParsedGuests]   = useState([]);
  // personStatuses: { [name]: { welcomeParty, attending, meal, dietary, dietaryNote } }
  const [personStatuses, setPersonStatuses] = useState({});
  const [submitted,      setSubmitted]      = useState(false);
  const [cleared,        setCleared]        = useState(false);
  const [locked,         setLocked]         = useState(() => isSearchLocked());

  const handleSearch = () => {
    if (locked) return;
    const q = query.trim().toLowerCase();
    if (!q) return;
    const nowLocked = incrementSearch();
    if (nowLocked) { setLocked(isSearchLocked()); return; }
    const matches = GUEST_LIST.filter(entry => {
      const g1 = entry.last.toLowerCase();
      const g2 = entry.partnerLast.toLowerCase();
      return g1.startsWith(q) || g2.startsWith(q);
    });
    setSearchResults(matches);
    setSearched(true);
    setSelectedEntry(null);
    setParsedGuests([]);
    setPersonStatuses({});
    setSubmitted(false);
    setCleared(false);
    if (matches.length === 1) handleSelectEntry(matches[0]);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handleSelectEntry = (entry) => {
    const guests = [];
    if (entry.first) guests.push(`${entry.first} ${entry.last}`);
    if (entry.partnerFirst && entry.partnerFirst !== 'Guest') {
      guests.push(`${entry.partnerFirst} ${entry.partnerLast}`);
    } else if (entry.partnerFirst === 'Guest') {
      guests.push('Guest');
    }
    const saved = loadAllRsvps();
    const statuses = {};
    guests.forEach(g => {
      statuses[g] = { ...EMPTY_STATUS, ...(saved[g] || {}) };
    });
    setSelectedEntry(entry);
    setParsedGuests(guests);
    setPersonStatuses(statuses);
    setSubmitted(guests.length > 0 && guests.every(g => isPersonComplete(saved[g], invitedToRehearsal(entry, g))));
    setCleared(false);
  };

  const handleClear = () => {
    setQuery(''); setSelectedEntry(null); setParsedGuests([]);
    setPersonStatuses({}); setSubmitted(false); setCleared(false);
    setSearchResults([]); setSearched(false);
  };

  const handlePersonField = (name, field, value) => {
    setPersonStatuses(prev => ({
      ...prev,
      [name]: { ...prev[name], [field]: value },
    }));
  };

  const handleSave = () => {
    const sanitized = {};
    parsedGuests.forEach(name => { sanitized[name] = sanitizeStatus(personStatuses[name], invitedToRehearsal(selectedEntry, name)); });
    setPersonStatuses(sanitized);
    savePartyRsvp(sanitized);
    savePartyRsvpRemote(selectedEntry.formal, sanitized);
    clearSearchTracker();
    setLocked(false);
    setSubmitted(true);
    setCleared(false);
  };

  const handleClearResponse = () => {
    const clearedStatuses = {};
    parsedGuests.forEach(name => { clearedStatuses[name] = { ...EMPTY_STATUS }; });
    clearPartyRsvp(parsedGuests);
    savePartyRsvpRemote(selectedEntry.formal, clearedStatuses);
    setPersonStatuses(clearedStatuses);
    setSubmitted(false);
    setCleared(true);
  };

  const allAnswered = parsedGuests.length > 0 && parsedGuests.every(g => isPersonComplete(personStatuses[g], invitedToRehearsal(selectedEntry, g)));
  const confirmMsg  = submitted ? getConfirmMessage(personStatuses) : '';

  return (
    <div className="site-wrapper">
      <div className="rsvp-fullpage">
        <div className="rsvp-bg-photo" style={{ backgroundImage: "url('/RSVP.png')" }} />
        <div className="rsvp-bg-overlay" />

        <div className="rsvp-content">
          <div className="rsvp-card">
            <div className="rsvp-save-the-date-row">
              <img
                src="/Flamingo Pointing.jpeg"
                alt="Flamingo pointing"
                className="rsvp-mascot rsvp-mascot--left"
              />
              <h2 style={{ fontSize: '60px', marginBottom: '0', lineHeight: 1.2, padding: 0 }}>RSVP</h2>
              <img
                src="/Gator Pointing.jpeg"
                alt="Gator pointing"
                className="rsvp-mascot rsvp-mascot--right"
              />
            </div>
            <div style={{ fontSize: '20px', color: 'grey', textAlign: 'center', marginBottom: '16px', fontFamily: 'var(--font-script)' }}>October 30, 2026 · Naples, Florida</div>
            <ul className="rsvp-intro-bullets">
              <li>We can't wait to celebrate with you in Naples — please respond for each guest below.</li>
              <li>We'll ask about the Welcome Party (Thursday, October 29), the Wedding (Friday, October 30), and your dinner selection.</li>
              <li>Responding for a group? You'll be able to RSVP for you and a guest (or your family) once you search your last name.</li>
            </ul>

            {/* ── Search ── */}
            {locked ? (
              <div className="rsvp-search-wrap">
                <div className="rsvp-lockout">
                  <p className="rsvp-lockout-heading">Please come back when you're ready to RSVP</p>
                  <p className="rsvp-lockout-body">
                    It looks like you've searched a few times without responding.
                    Please try again in an hour.
                  </p>
                </div>
              </div>
            ) : (
              <div className="rsvp-search-wrap">
                <label className="rsvp-search-label">Enter your last name</label>
                <div className="rsvp-search-row">
                  <div className="rsvp-search-field">
                    <input
                      className="rsvp-search-input"
                      type="text"
                      placeholder="Last name…"
                      value={query}
                      onChange={e => {
                        setQuery(e.target.value);
                        setSearched(false);
                        setSearchResults([]);
                        setSelectedEntry(null); setParsedGuests([]);
                        setPersonStatuses({}); setSubmitted(false); setCleared(false);
                      }}
                      onKeyDown={handleKeyDown}
                      autoComplete="off"
                      spellCheck="false"
                    />
                    {query && (
                      <button className="rsvp-search-clear" onClick={handleClear} aria-label="Clear">✕</button>
                    )}
                  </div>
                  <button className="btn btn-primary rsvp-search-btn" onClick={handleSearch}>
                    Search
                  </button>
                </div>

                {searched && !selectedEntry && searchResults.length > 1 && (
                  <div className="rsvp-multi-results">
                    <p className="rsvp-multi-label">We found multiple invitations — please select yours:</p>
                    {searchResults.map(entry => (
                      <button key={entry.formal} className="rsvp-multi-option" onClick={() => handleSelectEntry(entry)}>
                        {entry.formal}
                      </button>
                    ))}
                  </div>
                )}

                {searched && searchResults.length === 0 && (
                  <div className="rsvp-no-results">
                    No invitation found for that name — try a different spelling or reach out to us directly.
                  </div>
                )}
              </div>
            )}

            {/* ── Response area ── */}
            {selectedEntry && (
              <div className="rsvp-response-section">
                <div className="rsvp-response-divider" />

                {cleared ? (
                  /* ── Cleared ── */
                  <div className="rsvp-confirmed">
                    <div className="rsvp-confirmed-check" style={{ background: '#f3f4f6', color: '#9ca3af' }}>↺</div>
                    <p className="rsvp-confirmed-heading">Response Cleared</p>
                    <p className="rsvp-confirmed-msg">No worries — come back whenever you're ready!</p>
                  </div>
                ) : submitted ? (
                  /* ── Confirmed ── */
                  <div className="rsvp-confirmed">
                    <div className="rsvp-confirmed-check">✓</div>
                    <p className="rsvp-confirmed-heading">Response saved!</p>
                    <div className="rsvp-confirmed-rows">
                      {parsedGuests.map(name => {
                        const s = personStatuses[name] || {};
                        return (
                          <div key={name} className="rsvp-confirmed-person">
                            <div className="rsvp-confirmed-person-row">
                              <span className="rsvp-confirmed-person-name">{name}</span>
                              <span className={`rsvp-confirmed-badge rsvp-badge--${s.attending}`}>
                                {s.attending === 'accepts' ? 'Attending Wedding' : 'Unable to Attend Wedding'}
                              </span>
                            </div>
                            <div className="rsvp-confirmed-details">
                              {s.rehearsal && (
                                <span><strong>Rehearsal Dinner:</strong> {s.rehearsal === 'yes' ? 'Attending' : 'Not attending'}</span>
                              )}
                              <span><strong>Welcome Party:</strong> {s.welcomeParty === 'yes' ? 'Attending' : 'Not attending'}</span>
                              {s.attending === 'accepts' && s.meal && (
                                <span><strong>Dinner:</strong> {MEAL_LABEL[s.meal]}</span>
                              )}
                              {attendsAnyEvent(s) && s.dietary && (
                                <span><strong>Dietary:</strong> {s.dietary === 'yes' ? (s.dietaryNote || 'Yes') : 'None'}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="rsvp-confirmed-msg">{confirmMsg}</p>
                    <button className="rsvp-change-btn" onClick={() => setSubmitted(false)}>
                      Change Response
                    </button>
                    <button className="rsvp-clear-btn" onClick={handleClearResponse}>
                      Clear Response
                    </button>
                  </div>
                ) : (
                  /* ── Per-person form ── */
                  <>
                    <div className="rsvp-party-rows">
                      {parsedGuests.map(name => {
                        const s = personStatuses[name] || {};
                        return (
                          <div key={name} className="rsvp-person-block">
                            <div className="rsvp-person-name">{name}</div>

                            {/* Q0: Rehearsal Dinner — Wednesday (invited parties only) */}
                            {invitedToRehearsal(selectedEntry, name) && (
                              <div className="rsvp-question">
                                <div className="rsvp-question-label">
                                  Will you be attending the Rehearsal Dinner?
                                  <span className="rsvp-question-sub">Wednesday, October 28 · The Old Collier Golf Club · 5:00 PM</span>
                                </div>
                                <div className="rsvp-answer-row">
                                  <button
                                    className={`rsvp-answer-btn${s.rehearsal === 'yes' ? ' rsvp-answer-btn--active' : ''}`}
                                    onClick={() => handlePersonField(name, 'rehearsal', 'yes')}
                                  >
                                    Will Attend
                                  </button>
                                  <button
                                    className={`rsvp-answer-btn rsvp-answer-btn--decline${s.rehearsal === 'no' ? ' rsvp-answer-btn--decline-active' : ''}`}
                                    onClick={() => handlePersonField(name, 'rehearsal', 'no')}
                                  >
                                    Unable to Attend
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Q1: Welcome Party — Thursday */}
                            <div className="rsvp-question">
                              <div className="rsvp-question-label">
                                Will you be attending the Welcome Party?
                                <span className="rsvp-question-sub">Thursday, October 29 · LaPlaya Beach Club · 6:00 PM</span>
                              </div>
                              <div className="rsvp-answer-row">
                                <button
                                  className={`rsvp-answer-btn${s.welcomeParty === 'yes' ? ' rsvp-answer-btn--active' : ''}`}
                                  onClick={() => handlePersonField(name, 'welcomeParty', 'yes')}
                                >
                                  Will Attend
                                </button>
                                <button
                                  className={`rsvp-answer-btn rsvp-answer-btn--decline${s.welcomeParty === 'no' ? ' rsvp-answer-btn--decline-active' : ''}`}
                                  onClick={() => handlePersonField(name, 'welcomeParty', 'no')}
                                >
                                  Unable to Attend
                                </button>
                              </div>
                            </div>

                            {/* Q2: Wedding — Friday */}
                            <div className="rsvp-question">
                              <div className="rsvp-question-label">
                                Will you be attending the Wedding?
                                <span className="rsvp-question-sub">Friday, October 30 · Saint William Catholic Church · Reception at Naples Beach Club</span>
                              </div>
                              <div className="rsvp-answer-row">
                                <button
                                  className={`rsvp-answer-btn${s.attending === 'accepts' ? ' rsvp-answer-btn--active' : ''}`}
                                  onClick={() => handlePersonField(name, 'attending', 'accepts')}
                                >
                                  Accepts with Pleasure
                                </button>
                                <button
                                  className={`rsvp-answer-btn rsvp-answer-btn--decline${s.attending === 'declines' ? ' rsvp-answer-btn--decline-active' : ''}`}
                                  onClick={() => handlePersonField(name, 'attending', 'declines')}
                                >
                                  Declines with Regrets
                                </button>
                              </div>
                            </div>

                            {/* Q3: Dinner selection — only if attending the wedding */}
                            {s.attending === 'accepts' && (
                              <div className="rsvp-question">
                                <div className="rsvp-question-label">
                                  Please select your dinner entrée
                                </div>
                                <div className="rsvp-answer-row">
                                  {MEAL_OPTIONS.map(opt => (
                                    <button
                                      key={opt.value}
                                      className={`rsvp-answer-btn${s.meal === opt.value ? ' rsvp-answer-btn--active' : ''}`}
                                      onClick={() => handlePersonField(name, 'meal', opt.value)}
                                    >
                                      {opt.label}
                                      {opt.desc && <span className="rsvp-meal-desc">{opt.desc}</span>}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Q4: Dietary restrictions — if attending any event */}
                            {attendsAnyEvent(s) && (
                              <div className="rsvp-question">
                                <div className="rsvp-question-label">
                                  Do you have any dietary restrictions or allergies?
                                </div>
                                <div className="rsvp-answer-row">
                                  <button
                                    className={`rsvp-answer-btn${s.dietary === 'no' ? ' rsvp-answer-btn--active' : ''}`}
                                    onClick={() => handlePersonField(name, 'dietary', 'no')}
                                  >
                                    No Restrictions
                                  </button>
                                  <button
                                    className={`rsvp-answer-btn${s.dietary === 'yes' ? ' rsvp-answer-btn--active' : ''}`}
                                    onClick={() => handlePersonField(name, 'dietary', 'yes')}
                                  >
                                    Yes
                                  </button>
                                </div>
                                {s.dietary === 'yes' && (
                                  <textarea
                                    className="rsvp-dietary-input"
                                    rows={2}
                                    placeholder="Please share any allergies or dietary needs…"
                                    value={s.dietaryNote || ''}
                                    onChange={e => handlePersonField(name, 'dietaryNote', e.target.value)}
                                  />
                                )}
                              </div>
                            )}

                          </div>
                        );
                      })}
                    </div>

                    <button
                      className={`rsvp-save-btn${allAnswered ? ' rsvp-save-btn--ready' : ''}`}
                      onClick={handleSave}
                      disabled={!allAnswered}
                    >
                      Save Responses →
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
